/**
 * IMPORTS
 */
import { Check, X } from "lucide-react";

import { useResponderConvite } from "@/hooks/conversa-cliente/use-participantes-conversa";

import type { IStatusConvite } from "@/interface/conversa-cliente";
import { getApiErrorMessage } from "@/utils/normalize-api-error/normalize-api-error";
import { notifyError, notifySuccess } from "@/utils/notify-messages";

type AcoesConviteProps = {
  conviteId: number;
  status: IStatusConvite;
  /** Chamado depois de aceitar (ex.: abrir a conversa). */
  onAceito?: () => void;
};

const ROTULO_RESPONDIDO: Record<Exclude<IStatusConvite, "pendente">, string> = {
  aceito: "Convite aceito",
  recusado: "Convite recusado",
  inativo: "Acesso desativado",
};

/** Aceitar/recusar o convite para a conversa; depois de respondido, mostra só o resultado. */
function AcoesConvite({ conviteId, status, onAceito }: AcoesConviteProps) {
  const responder = useResponderConvite();

  if (status !== "pendente") {
    return <span className="text-xs font-medium text-gray-500">{ROTULO_RESPONDIDO[status]}</span>;
  }

  const enviar = (resposta: "aceitar" | "recusar") =>
    responder.mutate(
      { conviteId, resposta },
      {
        onSuccess: () => {
          if (resposta === "aceitar") {
            notifySuccess("Convite aceito! Você já pode participar da conversa.");
            onAceito?.();
          } else {
            notifySuccess("Convite recusado.");
          }
        },
        onError: (erro) => notifyError(getApiErrorMessage(erro)),
      }
    );

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={responder.isPending}
        onClick={() => enviar("aceitar")}
        className="flex items-center gap-1 rounded-md bg-orange-600 px-3 py-1 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-50"
      >
        <Check className="size-3.5" />
        Aceitar
      </button>
      <button
        type="button"
        disabled={responder.isPending}
        onClick={() => enviar("recusar")}
        className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        <X className="size-3.5" />
        Recusar
      </button>
    </div>
  );
}

/**
 * EXPORTS
 */
export { AcoesConvite };
